/**
 * Single open-paint gate for BioChat.
 *
 * Phases:
 * 1. critical — visible viewport SoT only: projects list + snapshot +
 *    storage/index (files) + plans/active (plan)
 * 2. settle — same paint URLs continue; block stream-status/code/node-light
 *    while those three panels finish painting
 * 3. steady — tiered slots (paint / default / background) + GET coalesce
 */

let critical = false;
let allowDiscovery = false;
let waiters = [];

let settling = false;
let settleWaiters = [];
let settleTimer = null;

const PAINT_CONCURRENCY = 3;
const DEFAULT_CONCURRENCY = 3;
const BACKGROUND_CONCURRENCY = 1;
const DEFAULT_SETTLE_MS = 2000;

let activePaintSlots = 0;
let paintWaiters = [];
let activeDefaultSlots = 0;
let defaultWaiters = [];
let activeBackgroundSlots = 0;
let backgroundWaiters = [];

/** @type {Map<string, Promise<unknown>>} */
const inflightGets = new Map();

export function beginOmicsOpenCritical({ discovery = false } = {}) {
  critical = true;
  allowDiscovery = Boolean(discovery);
  // New open cancels any prior settle.
  if (settleTimer) {
    clearTimeout(settleTimer);
    settleTimer = null;
  }
  settling = false;
  if (settleWaiters.length) {
    const pending = settleWaiters;
    settleWaiters = [];
    pending.forEach((resolve) => resolve());
  }
}

export function beginOmicsViewportSettle(ms = DEFAULT_SETTLE_MS) {
  settling = true;
  if (settleTimer) {
    clearTimeout(settleTimer);
  }
  settleTimer = setTimeout(() => {
    settleTimer = null;
    settling = false;
    if (settleWaiters.length) {
      const pending = settleWaiters;
      settleWaiters = [];
      pending.forEach((resolve) => resolve());
    }
  }, Math.max(0, Number(ms) || 0));
}

export function endOmicsOpenCritical({ settleMs = DEFAULT_SETTLE_MS } = {}) {
  const wasCritical = critical;
  if (critical) {
    critical = false;
    allowDiscovery = false;
    if (waiters.length) {
      const pending = waiters;
      waiters = [];
      pending.forEach((resolve) => resolve());
    }
  }
  if (wasCritical && settleMs > 0) {
    beginOmicsViewportSettle(settleMs);
  }
}

export function isOmicsOpenCritical() {
  return critical;
}

export function isOmicsViewportSettling() {
  return settling;
}

export function waitForOmicsOpenCriticalEnd() {
  if (!critical) {
    return Promise.resolve();
  }
  return new Promise((resolve) => {
    waiters.push(resolve);
  });
}

export function waitForOmicsViewportSettleEnd() {
  if (!settling) {
    return Promise.resolve();
  }
  return new Promise((resolve) => {
    settleWaiters.push(resolve);
  });
}

/**
 * Viewport SoT only — keep this narrow. Unlimited "paint" fan-out previously
 * stampeded local Django and pushed plan/chat `li` LCP past 10s.
 */
export function isOmicsPaintPriorityUrl(url) {
  const u = String(url || '');
  if (!u.includes('/api/admin/omics/')) return false;
  if (u.includes('/snapshot/')) return true;
  if (u.includes('/storage/index/')) return true;
  if (u.includes('/plans/active')) return true;
  // Lean projects list — critical first-paint SoT (PG-backed).
  if (/\/omics\/projects\/?(?:\?|$)/.test(u)) return true;
  return false;
}

/** Continuous polls — must not occupy the same slots as viewport GETs. */
export function isOmicsBackgroundUrl(url) {
  const u = String(url || '');
  if (!u.includes('/api/admin/omics/')) return false;
  if (u.includes('/stream-status/')) return true;
  if (u.includes('/predict-activity/')) return true;
  if (u.includes('/containers/')) return true;
  if (u.includes('/sessions/')) return true;
  return false;
}

export function isAllowedDuringOmicsViewportSettle(url, method = 'GET') {
  const m = String(method || 'GET').toUpperCase();
  if (m !== 'GET') return false;
  return isOmicsPaintPriorityUrl(url);
}

async function acquirePaintSlot() {
  while (activePaintSlots >= PAINT_CONCURRENCY) {
    await new Promise((resolve) => {
      paintWaiters.push(resolve);
    });
  }
  activePaintSlots += 1;
}

function releasePaintSlot() {
  activePaintSlots = Math.max(0, activePaintSlots - 1);
  if (paintWaiters.length) {
    const next = paintWaiters.shift();
    next();
  }
}

async function acquireDefaultSlot() {
  while (activeDefaultSlots >= DEFAULT_CONCURRENCY) {
    await new Promise((resolve) => {
      defaultWaiters.push(resolve);
    });
  }
  activeDefaultSlots += 1;
}

function releaseDefaultSlot() {
  activeDefaultSlots = Math.max(0, activeDefaultSlots - 1);
  if (defaultWaiters.length) {
    const next = defaultWaiters.shift();
    next();
  }
}

async function acquireBackgroundSlot() {
  while (activeBackgroundSlots >= BACKGROUND_CONCURRENCY) {
    await new Promise((resolve) => {
      backgroundWaiters.push(resolve);
    });
  }
  activeBackgroundSlots += 1;
}

function releaseBackgroundSlot() {
  activeBackgroundSlots = Math.max(0, activeBackgroundSlots - 1);
  if (backgroundWaiters.length) {
    const next = backgroundWaiters.shift();
    next();
  }
}

/**
 * Coalesce identical in-flight omics GETs (same URL).
 * Callers share one network round-trip and one response object.
 */
export async function withOmicsGetCoalesce(url, method, fn) {
  const m = String(method || 'GET').toUpperCase();
  const key = String(url || '');
  if (m !== 'GET' || !key.includes('/api/admin/omics/')) {
    return fn();
  }
  const existing = inflightGets.get(key);
  if (existing) {
    return existing;
  }
  const pending = Promise.resolve()
    .then(fn)
    .finally(() => {
      if (inflightGets.get(key) === pending) {
        inflightGets.delete(key);
      }
    });
  inflightGets.set(key, pending);
  return pending;
}

/**
 * Run ``fn`` under the post-paint omics concurrency lanes.
 * Snapshot always bypasses so chat paint is never queued.
 */
export async function withOmicsRequestSlot(url, fn) {
  const u = String(url || '');
  if (u.includes('/snapshot/')) {
    return fn();
  }
  if (isOmicsPaintPriorityUrl(u)) {
    await acquirePaintSlot();
    try {
      return await fn();
    } finally {
      releasePaintSlot();
    }
  }
  if (isOmicsBackgroundUrl(u)) {
    await acquireBackgroundSlot();
    try {
      return await fn();
    } finally {
      releaseBackgroundSlot();
    }
  }
  await acquireDefaultSlot();
  try {
    return await fn();
  } finally {
    releaseDefaultSlot();
  }
}

/**
 * Allowed during open critical.
 * - Always: lean projects list + visible viewport SoT (snapshot, files, plan)
 * - If discovery: single project GET + node chats list
 */
export function isAllowedDuringOmicsOpenCritical(url, method = 'GET') {
  const u = String(url || '');
  const m = String(method || 'GET').toUpperCase();
  if (m !== 'GET') return false;
  if (!u.includes('/api/admin/omics/')) return true;
  if (u.includes('/snapshot/')) return true;
  if (u.includes('/storage/index/')) return true;
  if (u.includes('/plans/active')) return true;
  // Org projects list (not /projects/{id}/).
  if (/\/omics\/projects\/?(?:\?|$)/.test(u)) return true;
  if (!allowDiscovery) return false;
  if (/\/omics\/projects\/[^/?]+\/?(?:\?|$)/.test(u)) return true;
  if (/\/nodes\/[^/]+\/chats\/?(?:\?|$)/.test(u)) return true;
  return false;
}

/** Test helper — reset module state between tests. */
export function __resetOmicsOpenCriticalForTests() {
  critical = false;
  allowDiscovery = false;
  waiters = [];
  settling = false;
  settleWaiters = [];
  if (settleTimer) {
    clearTimeout(settleTimer);
    settleTimer = null;
  }
  activePaintSlots = 0;
  paintWaiters = [];
  activeDefaultSlots = 0;
  defaultWaiters = [];
  activeBackgroundSlots = 0;
  backgroundWaiters = [];
  inflightGets.clear();
}
